/**
 * Platform-level settings page: account info + push notification preferences.
 * Accessible from the gear icon in the PlatformShell header at /settings.
 */

import { ArrowLeft, User } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { NotificationSettings } from '@/components/settings/NotificationSettings';
import { PlatformBackground } from '@/components/platform/PlatformBackground';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';

export default function PlatformSettings() {
  const { user } = useAuthStore();

  return (
    <div className="relative flex min-h-full flex-col">
      <PlatformBackground variant="launcher" />
      <div className="relative z-10 mx-auto w-full max-w-2xl space-y-6 p-6">
        <Link
          to="/"
          aria-label="Back to home"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground">Platform preferences and notifications</p>
        </div>

        {/* Account */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Account
            </CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Username</label>
              <input
                type="text"
                value={user?.username || 'User'}
                disabled
                className={cn(
                  'flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                value={user?.email || 'user@example.com'}
                disabled
                className={cn(
                  'flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Push Notification Preferences */}
        <NotificationSettings />
      </div>
    </div>
  );
}
